import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import {
  AlertPosition,
  AlertType,
  PdfLayout,
  ProjectElementType,
  TextAlignment,
} from '../../shared/enum/EnumRepository';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';
import logo from '../../asset/gnb/logo_fit.png';
import defaultImg from '../../asset/project/default_1.png';
import { ProjectElementData } from '../../shared/dto/EntityRepository';
import { getModalMessage } from '../../shared/constants/modalMessages';
import type { FloorPlanCaptionLayout } from './floorPlanA4PreviewTypes';
import { getFloorPlanCaptionSlotPlan } from './floorPlanCaptionLimits';
import FloorPlanA4PreviewVertical from './FloorPlanA4PreviewVertical';
import FloorPlanA4PreviewHorizontal from './FloorPlanA4PreviewHorizontal';
import { sizeCheckBig } from '../../shared/util/fileSizeCheck';
import { FILE_MAX_SIZE, FILE_MAX_SIZE_TEXT } from '../../shared/constants/limits';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';
import { pdf } from '@react-pdf/renderer';
import DocumentFloorPlanVertical from './DocumentFloorPlanVertical';
import DocumentFloorPlanHorizontal from './DocumentFloorPlanHorizontal';
import { resolveFloorPlanImageSrcForPdf } from './documentFloorPlanPdfShared';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useSettingStore } from '../../shared/store/settingStore';
import { shouldShowWorkArtist } from '../../shared/util/workArtist';
import { filterUnsupportedImageFiles } from '../../shared/util/imageFileFormat';

export interface FloorPlanModalCreateProps {
  peWorkList: ProjectElementData[];
  onBack: () => void;
  setIsUserInput: (isUserInput: boolean) => void;
}

const FloorPlanModalCreate: React.FC<FloorPlanModalCreateProps> = ({
  peWorkList,
  onBack,
  setIsUserInput,
}) => {
  const { enqueueAlert } = useStandardAlertStore();
  const { project } = useProjectDetailState();
  const setting = useSettingStore((state) => state.setting);
  const includeArtist = shouldShowWorkArtist(setting);
  const floorImageObjectUrlRef = useRef<string | null>(null);

  const [title, setTitle] = useState(() => project?.title ?? '');
  const [subtitle, setSubtitle] = useState('');
  const [date, setDate] = useState('');
  const [gallery, setGallery] = useState('');
  const [location, setLocation] = useState('');
  const [img, setImg] = useState(defaultImg);
  const [captionLayout, setCaptionLayout] = useState<FloorPlanCaptionLayout>('column');
  const [pdfSheetLayout, setPdfSheetLayout] = useState<PdfLayout>(PdfLayout.Vertical);

  const projectTitleBaseline = project?.title ?? '';

  const isFloorPlanFormDirty =
    title !== projectTitleBaseline ||
    subtitle !== '' ||
    date !== '' ||
    gallery !== '' ||
    location !== '' ||
    captionLayout !== 'column' ||
    pdfSheetLayout !== PdfLayout.Vertical ||
    img !== defaultImg;

  const revokeFloorImageObjectUrl = useCallback(() => {
    if (floorImageObjectUrlRef.current) {
      URL.revokeObjectURL(floorImageObjectUrlRef.current);
      floorImageObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    setTitle(project?.title ?? '');
    setSubtitle('');
    setDate('');
    setGallery('');
    setLocation('');
    setCaptionLayout('column');
    setPdfSheetLayout(PdfLayout.Vertical);
    setImg(defaultImg);
    revokeFloorImageObjectUrl();
  }, [project?.id, revokeFloorImageObjectUrl]);

  useEffect(() => {
    setIsUserInput(isFloorPlanFormDirty);
  }, [isFloorPlanFormDirty, setIsUserInput]);

  useEffect(() => {
    return () => {
      revokeFloorImageObjectUrl();
    };
  }, [revokeFloorImageObjectUrl]);

  const handleFloorImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && !filterUnsupportedImageFiles([file]).length) return;
    if (!file?.type.startsWith('image/')) return;
    if (!sizeCheckBig(file, FILE_MAX_SIZE)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('FILE_TOO_BIG_PREVIEW', { maxSize: FILE_MAX_SIZE_TEXT }),
      });
      return;
    }
    revokeFloorImageObjectUrl();
    const url = URL.createObjectURL(file);
    floorImageObjectUrlRef.current = url;
    setImg(url);
  };

  const captionSegmentCounts = useMemo(() => {
    const { maxSlots, segmentChunkSize } = getFloorPlanCaptionSlotPlan(
      pdfSheetLayout,
      captionLayout
    );
    const n = Math.min(peWorkList.length, maxSlots);
    if (n === 0) return [];
    const counts: number[] = [];
    for (let i = 0; i < n; i += segmentChunkSize) {
      counts.push(Math.min(segmentChunkSize, n - i));
    }
    return counts;
  }, [peWorkList.length, captionLayout, pdfSheetLayout]);

  const sanitizeFloorPlanFileStem = (value: string) =>
    value.replace(/[/\\?%*:|"<>]/g, '-').trim().slice(0, 80) || 'Floor_plan';

  const handleSubmit = async () => {
    try {
      const { maxSlots } = getFloorPlanCaptionSlotPlan(pdfSheetLayout, captionLayout);
      const workElements = peWorkList.filter(
        (pe) => pe.projectElementType === ProjectElementType.WORK && pe.work != null
      );
      const FloorPlanDoc =
        pdfSheetLayout === PdfLayout.Vertical ? DocumentFloorPlanVertical : DocumentFloorPlanHorizontal;
      /** `blob:`/`data:` → JPEG data URL (WebP 등 @react-pdf 미지원 포맷 포함) */
      const floorImageForPdf = await resolveFloorPlanImageSrcForPdf(img);
      const blob = await pdf(
        <FloorPlanDoc
          title={title}
          subtitle={subtitle}
          date={date}
          gallery={gallery}
          location={location}
          pageNumberLabel="1 / 1"
          floorImageSrc={floorImageForPdf}
          workElements={workElements.slice(0, maxSlots)}
          captionLayout={captionLayout}
          includeArtist={includeArtist}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Floor_plan_${sanitizeFloorPlanFileStem(title)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('NETWORK_ERROR'),
      });
    }
  };

  return (
    <>
      {pdfSheetLayout === PdfLayout.Vertical ? (
        <FloorPlanA4PreviewVertical
          logoSrc={logo}
          title={title}
          subtitle={subtitle}
          date={date}
          imageSrc={img}
          gallery={gallery}
          location={location}
          pageNumberLabel="1 / 1"
          captionLayout={captionLayout}
          captionSegmentCounts={captionSegmentCounts}
          onFloorImageFileChange={handleFloorImageFileChange}
        />
      ) : (
        <FloorPlanA4PreviewHorizontal
          logoSrc={logo}
          title={title}
          subtitle={subtitle}
          date={date}
          imageSrc={img}
          gallery={gallery}
          location={location}
          pageNumberLabel="1 / 1"
          captionLayout={captionLayout}
          captionSegmentCounts={captionSegmentCounts}
          onFloorImageFileChange={handleFloorImageFileChange}
        />
      )}
      <PDFInfo>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('ABOUT_PDF_LAYOUT_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="floorPlanPdfSheetLayout"
                checked={pdfSheetLayout === PdfLayout.Vertical}
                onChange={() => setPdfSheetLayout(PdfLayout.Vertical)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_PORTRAIT')}
            </CheckboxComp>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="floorPlanPdfSheetLayout"
                checked={pdfSheetLayout === PdfLayout.Horizontal}
                onChange={() => setPdfSheetLayout(PdfLayout.Horizontal)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_LANDSCAPE')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_TITLE_LABEL')}</PDFInfoLabel>
          <HeadlessInput
            value={title}
            placeholder={getModalMessage('FLOOR_PLAN_FORM_TITLE_PLACEHOLDER')}
            handleChange={(e) => setTitle(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_SUBTITLE_LABEL')}</PDFInfoLabel>
          <HeadlessInput
            value={subtitle}
            placeholder={getModalMessage('FLOOR_PLAN_FORM_SUBTITLE_PLACEHOLDER')}
            handleChange={(e) => setSubtitle(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_DATE_LABEL')}</PDFInfoLabel>
          <HeadlessInput
            value={date}
            placeholder={getModalMessage('FLOOR_PLAN_FORM_DATE_PLACEHOLDER')}
            handleChange={(e) => setDate(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_GALLERY_LABEL')}</PDFInfoLabel>
          <HeadlessInput
            value={gallery}
            placeholder={getModalMessage('FLOOR_PLAN_FORM_GALLERY_PLACEHOLDER')}
            handleChange={(e) => setGallery(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_LOCATION_LABEL')}</PDFInfoLabel>
          <HeadlessInput
            value={location}
            placeholder={getModalMessage('FLOOR_PLAN_FORM_LOCATION_PLACEHOLDER')}
            handleChange={(e) => setLocation(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('FLOOR_PLAN_FORM_CAPTION_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="floorPlanCaptionStyle"
                checked={captionLayout === 'column'}
                onChange={() => setCaptionLayout('column')}
              />
              {getModalMessage('FLOOR_PLAN_FORM_CAPTION_COLUMN')}
            </CheckboxComp>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="floorPlanCaptionStyle"
                checked={captionLayout === 'row'}
                onChange={() => setCaptionLayout('row')}
              />
              {getModalMessage('FLOOR_PLAN_FORM_CAPTION_ROW')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
      </PDFInfo>
      <ButtonContainer>
        <HeadlessBtn
          value={getBtnName('BEFORE')}
          handleClick={onBack}
          StyledBtn={PaperBlurFitBtn}
        />
        <HeadlessBtn
          value={getBtnName('GENERATE')}
          handleClick={handleSubmit}
          StyledBtn={BlackFitBtn}
        />
      </ButtonContainer>
    </>
  );
};

const PDFInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const PDFInfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const PDFInfoLabel = styled.span`
  min-width: 70px;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1vw;
  flex-wrap: wrap;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const RadioBox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default FloorPlanModalCreate;
